import { Helmet } from "react-helmet-async";
import { BreadcrumbsJsonLd } from "@/components/seo/BreadcrumbsJsonLd";
import { ArticleJsonLd } from "@/components/seo/ArticleJsonLd";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, AlertTriangle, Stethoscope, Scale, Activity } from "lucide-react";
import { publicClinicalData } from "@/data/peptide-clinical-data";
import { GLP1_DISQUALIFYING_CONDITIONS } from "@/lib/glp1-screening";
import { APP_URL, CANONICAL_ORIGIN, canonicalUrl } from "@/lib/app-config";

const PAGE_PATH = "/peptides/weight-loss";
const PAGE_TITLE = "Peptides for Weight Loss: A Clinician's Guide | Elara Health";
const PAGE_DESCRIPTION =
  "How metabolic peptides like GLP-1 medications support weight loss, how they differ from diet-and-exercise alone, who qualifies, and what a physician-guided protocol involves.";

const weightCategory = publicClinicalData.find((cat) => cat.id === "weight");
const weightPeptides = weightCategory?.peptides ?? [];

const comparisonRows = [
  {
    factor: "Primary mechanism",
    traditional: "Willpower-driven calorie deficit; appetite signals stay unchanged",
    peptide: "Acts on the appetite and glucose-regulating pathways your gut and brain already use",
  },
  {
    factor: "What hunger feels like",
    traditional: "Hunger and food preoccupation typically rise as weight comes down",
    peptide: "Patients commonly report reduced appetite and quieter food cravings",
  },
  {
    factor: "Medical oversight",
    traditional: "Usually self-directed, with no lab work or monitoring",
    peptide: "Prescription-only, with screening, lab testing, and dose titration by a clinician",
  },
  {
    factor: "Metabolic markers",
    traditional: "Improve with weight loss, but are rarely measured",
    peptide: "Blood sugar and insulin sensitivity are monitored alongside weight",
  },
  {
    factor: "Sustainability",
    traditional: "Habits are the whole plan, and regain is common when they slip",
    peptide: "Medication supports the habits — nutrition, protein, and strength work still matter",
  },
];

const faqs = [
  {
    q: "What are weight loss peptides?",
    a: "They are prescription molecules made of amino acids that act as signals in your body. The best-known group are GLP-1 receptor agonists such as semaglutide, which mimic a gut hormone that regulates appetite and blood sugar. Because they work through your own metabolic pathways rather than stimulants, they are prescribed and monitored by a licensed clinician.",
  },
  {
    q: "How are GLP-1 peptides different from dieting alone?",
    a: "Dieting asks you to override hunger signals; GLP-1 therapy changes the signals themselves. That is why many patients find a calorie deficit easier to sustain on treatment. It is not a replacement for nutrition and strength training — those protect muscle and metabolic health while you lose fat.",
  },
  {
    q: "How quickly do results appear?",
    a: "Metabolic peptides are titrated slowly, starting at a low dose and stepping up over weeks to limit side effects. Most protocols are evaluated over months rather than weeks, with your clinician adjusting the dose based on your response and tolerability. We do not promise a specific number on the scale — your results depend on your starting point, adherence, and physiology.",
  },
  {
    q: "What are the common side effects?",
    a: "The most frequently reported effects are gastrointestinal — nausea, reduced appetite, constipation, or reflux — and they are usually most noticeable after a dose increase. Mild irritation at the injection site is also common. Your clinician reviews the full risk profile with you before prescribing and adjusts your plan if side effects persist.",
  },
  {
    q: "Who should not take GLP-1 medications?",
    a: `Screening matters. Elara Health does not prescribe GLP-1 therapy to patients who report ${GLP1_DISQUALIFYING_CONDITIONS.slice(0, -1).join(", ").toLowerCase()}, or ${GLP1_DISQUALIFYING_CONDITIONS[GLP1_DISQUALIFYING_CONDITIONS.length - 1].toLowerCase()}. Recent bariatric surgery, pregnancy or breastfeeding, and a history of substance misuse are also reviewed by a clinician before any prescription is written.`,
  },
  {
    q: "Do I keep the weight off after stopping?",
    a: "Appetite regulation returns toward its prior baseline once treatment ends, so weight regain is possible. Your clinician plans for this from the start — building nutrition, protein intake, and resistance training into the protocol, and discussing maintenance dosing or a structured taper rather than an abrupt stop.",
  },
];

const WeightLossPeptides = () => {
  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{PAGE_TITLE}</title>
        <meta name="description" content={PAGE_DESCRIPTION} />
        <link rel="canonical" href={canonicalUrl(PAGE_PATH)} />
        <meta property="og:title" content={PAGE_TITLE} />
        <meta property="og:description" content={PAGE_DESCRIPTION} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl(PAGE_PATH)} />
        <meta property="og:image" content={`${APP_URL}/og-image.jpg`} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "MedicalWebPage",
                "@id": canonicalUrl(PAGE_PATH),
                name: "Peptides for Weight Loss",
                description: PAGE_DESCRIPTION,
                url: canonicalUrl(PAGE_PATH),
                mainEntityOfPage: { "@id": canonicalUrl(PAGE_PATH) },
                specialty: "Endocrinology",
                inLanguage: "en-US",
                isPartOf: { "@id": `${CANONICAL_ORIGIN}/#website` },
                publisher: { "@id": `${CANONICAL_ORIGIN}/#organization` },
                about: weightPeptides.map((p) => ({
                  "@type": "Drug",
                  name: p.name,
                  description: p.simple,
                })),
              },
              {
                "@type": "FAQPage",
                mainEntity: faqs.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          })}
        </script>
      </Helmet>
      <BreadcrumbsJsonLd items={[{ name: "Peptide Therapy", path: "/peptides" }, { name: "Weight Loss" }]} />
      <ArticleJsonLd
        path={PAGE_PATH}
        headline="Peptides for Weight Loss: A Clinician's Guide"
        description={PAGE_DESCRIPTION}
        datePublished="2026-08-10"
        dateModified="2026-08-10"
        section="Weight Management"
        keywords={["weight loss peptides", "GLP-1", "semaglutide", "tirzepatide", "metabolic health"]}
      />

      <Navbar />

      <div className="pt-24 pb-16">
        {/* Hero */}
        <section className="container mx-auto px-4 mb-14">
          <div className="max-w-3xl mx-auto space-y-5">
            <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
              <Link to="/" className="hover:text-foreground transition-colors">
                Home
              </Link>
              <span className="mx-2">/</span>
              <Link to="/peptides" className="hover:text-foreground transition-colors">
                Peptide Therapy
              </Link>
              <span className="mx-2">/</span>
              <span className="text-foreground">Weight Loss</span>
            </nav>
            <p className="text-xs tracking-[0.2em] uppercase text-primary">Clinical Guide</p>
            <h1 className="font-display text-4xl md:text-5xl text-foreground">
              Peptides for Weight Loss
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Metabolic peptides — the group that includes GLP-1 medications — work by adjusting the
              appetite and blood-sugar signals your body already sends, rather than by stimulating
              your metabolism. This guide explains how they work, how they compare with dieting
              alone, who qualifies, and what physician-guided treatment actually looks like.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/get-started">
                <Button size="lg" className="gap-2">
                  Check your eligibility <ArrowRight size={18} />
                </Button>
              </Link>
              <Link to="/peptides">
                <Button size="lg" variant="outline">
                  Browse all peptide therapies
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* How they work */}
        <section className="container mx-auto px-4 mb-14">
          <div className="max-w-3xl mx-auto space-y-5">
            <h2 className="font-display text-3xl text-foreground">
              How metabolic peptides support weight loss
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Peptides are short chains of amino acids that act as messengers. GLP-1 (glucagon-like
              peptide-1) is one your gut releases after you eat; it slows gastric emptying, signals
              fullness to the brain, and helps the pancreas release insulin in response to glucose.
              GLP-1 receptor agonists such as semaglutide mimic that hormone for far longer than the
              natural version lasts, so the fullness signal stays switched on between meals.
            </p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                {
                  icon: Scale,
                  label: "Appetite regulation",
                  desc: "Fullness signals persist longer, so portions and cravings shrink without constant willpower.",
                },
                {
                  icon: Activity,
                  label: "Glucose control",
                  desc: "Insulin is released in response to glucose, steadying the swings that drive hunger.",
                },
                {
                  icon: Stethoscope,
                  label: "Clinical titration",
                  desc: "Dosing starts low and steps up over weeks, guided by labs and how you tolerate it.",
                },
              ].map((item) => (
                <Card key={item.label} className="border-border bg-card">
                  <CardContent className="p-5 space-y-2">
                    <item.icon size={18} className="text-primary" />
                    <p className="font-display text-base text-foreground">{item.label}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-muted-foreground leading-relaxed">
              Newer agents extend the same idea to additional receptors. Tirzepatide acts on both GIP
              and GLP-1 pathways, while tesamorelin takes a different route entirely — stimulating
              your natural growth hormone release to reduce deep abdominal fat.
            </p>
          </div>
        </section>

        {/* Comparison */}
        <section className="container mx-auto px-4 mb-14">
          <div className="max-w-4xl mx-auto space-y-5">
            <h2 className="font-display text-3xl text-foreground">
              Peptide therapy vs. diet and exercise alone
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              Metabolic peptides are not a substitute for the fundamentals — they change how
              achievable the fundamentals feel.
            </p>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <caption className="sr-only">
                  Comparison of traditional weight loss methods and physician-guided peptide therapy
                </caption>
                <thead>
                  <tr className="bg-secondary/50 text-left">
                    <th scope="col" className="p-4 font-display font-normal text-foreground">
                      Factor
                    </th>
                    <th scope="col" className="p-4 font-display font-normal text-foreground">
                      Diet &amp; exercise alone
                    </th>
                    <th scope="col" className="p-4 font-display font-normal text-foreground">
                      Physician-guided peptide therapy
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row) => (
                    <tr key={row.factor} className="border-t border-border align-top">
                      <th scope="row" className="p-4 text-left font-medium text-foreground">
                        {row.factor}
                      </th>
                      <td className="p-4 text-muted-foreground">{row.traditional}</td>
                      <td className="p-4 text-muted-foreground">{row.peptide}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Therapies */}
        {weightPeptides.length > 0 && (
          <section className="container mx-auto px-4 mb-14">
            <div className="max-w-4xl mx-auto space-y-5">
              <h2 className="font-display text-3xl text-foreground">
                Weight management therapies we discuss openly
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                {weightCategory?.description}
              </p>
              <div className="grid md:grid-cols-2 gap-4">
                {weightPeptides.map((p) => (
                  <Card key={p.name} className="border-border bg-card">
                    <CardContent className="p-5 space-y-2">
                      <h3 className="font-display text-lg text-foreground">{p.name}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{p.simple}</p>
                      <p className="text-xs text-muted-foreground/80">
                        <span className="text-foreground">How it works:</span> {p.mechanism}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
              <p className="text-xs text-muted-foreground italic">
                Additional weight management protocols are reviewed with a licensed clinician inside
                your patient portal.
              </p>
            </div>
          </section>
        )}

        {/* Eligibility */}
        <section className="container mx-auto px-4 mb-14">
          <Card className="max-w-4xl mx-auto border-border bg-secondary/30">
            <CardContent className="p-8">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-1">
                  <AlertTriangle size={20} className="text-primary" />
                </div>
                <div className="space-y-3">
                  <h2 className="font-display text-xl text-foreground">Who is screened out</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    GLP-1 therapy is not appropriate for everyone. Our intake assessment screens for
                    each of the following, and a licensed clinician reviews every answer before any
                    prescription is written:
                  </p>
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-muted-foreground">
                    {GLP1_DISQUALIFYING_CONDITIONS.map((condition) => (
                      <li key={condition} className="flex gap-2">
                        <span className="text-primary" aria-hidden="true">
                          •
                        </span>
                        {condition}
                      </li>
                    ))}
                  </ul>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Recent bariatric surgery, pregnancy or breastfeeding, and a history of substance
                    misuse are also flagged for clinician review. Treatment is only available in
                    states where one of our licensed providers holds an active license.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* FAQ */}
        <section className="container mx-auto px-4 mb-14">
          <div className="max-w-3xl mx-auto space-y-5">
            <h2 className="font-display text-3xl text-foreground">Frequently asked questions</h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`}>
                  <AccordionTrigger className="text-left font-display text-base text-foreground">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center space-y-4">
            <h2 className="font-display text-3xl text-foreground">
              See whether peptide therapy fits you
            </h2>
            <p className="text-muted-foreground">
              Take a 5-minute health assessment. A licensed clinician reviews your answers and
              designs a personalized protocol — or tells you plainly if treatment is not appropriate.
            </p>
            <Link to="/get-started">
              <Button size="lg" className="gap-2">
                Start Your Assessment <ArrowRight size={18} />
              </Button>
            </Link>
            <p className="text-xs text-muted-foreground">
              This page is general education, not medical advice. Read our{" "}
              <Link to="/medical-disclaimer" className="underline hover:text-foreground">
                medical disclaimer
              </Link>
              .
            </p>
          </div>
        </section>
      </div>

      <Footer />
    </div>
  );
};

export default WeightLossPeptides;
